import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import { Trash2, Info } from 'react-feather'

import Loading from '../../../templates/UI/Loading/Loading'
import callAPI from '../../../helpers/callAPI'
import Campaign from '../../../templates/Campaign/Campaign'
import CampaignSearch from '../../../components/CampaignSearch/CampaignSearch'
import CustomButton from '../../../templates/UI/CustomButton/CustomButton'
import Checkbox from '../../Reports/Filters/Checkbox/Checkbox'
import { useAuth, useProtectedContent } from '../../../context/AuthContext'

const CampaignPitches = (props) => {
    const auth = useAuth()
    const showOverrideToggle = useProtectedContent(auth.permissions, 'affiliate_pitch_override')

    const [dataLoaded, setDataLoaded] = useState(false)
    const [selectedOffer, setSelectedOffer] = useState()
    const [focusedOffer, setFocusedOffer] = useState()
    const [pitchList, setPitchList] = useState([])
    const [followupModalData, setFollowupModalData] = useState(null)
    const [showRetroactiveModal, setShowRetroactiveModal] = useState(false)
    const [retroactiveCampaign, setRetroactiveCampaign] = useState()
    const [retroactiveDate, setRetroactiveDate] = useState('')
    const [retroactiveNote, setRetroactiveNote] = useState('')
    const [retroactiveSubmitting, setRetroactiveSubmitting] = useState(false)

    const { showSystemMessage, affiliateId, refreshKey } = props

    useEffect(() => {
        let startDate = moment().subtract(3, 'months').format('YYYY-MM-DD HH:mm:ss')
        let endDate = moment().format('YYYY-MM-DD HH:mm:ss')

        let url = `affiliate/${affiliateId}/pitches?startDate=${startDate}&endDate=${endDate}`

        callAPI(url, 'GET')
            .then((result) => {
                if (result['success'] === true) {
                    setPitchList(result.pitch_list)
                    setDataLoaded(true)
                } else if (result['success'] === false) {
                    if (result.code !== 401) {
                        showSystemMessage('error', 'There was an error while fetching the data. Please try again')
                    }
                } else {
                    showSystemMessage('error', result.errors.message)
                }
            })
            .catch((error) => {
                console.log('Error in fetching campaign pitches: ', error)
                showSystemMessage('error', 'Connection Error')
            })
    }, [dataLoaded, refreshKey])

    const handleMouseEnter = (value) => {
        setFocusedOffer(value)
    }

    const handleMouseLeave = () => {
        setFocusedOffer()
        setSelectedOffer()
    }

    const handleDelete = (index) => {
        setSelectedOffer(index)
    }

    const handleViewFollowupNote = (item) => {
        const noteText = item.followup_note ? item.followup_note.split('\n').join('<br/>').trim() : ''
        setFollowupModalData({ note: noteText, name: item.name, offerId: item.offer_id, date: item.db_date })
    }

    const closeFollowupNoteModal = () => {
        setFollowupModalData(null)
    }

    const closeRetroactiveModal = () => {
        setShowRetroactiveModal(false)
        setRetroactiveCampaign()
        setRetroactiveDate('')
        setRetroactiveNote('')
        setRetroactiveSubmitting(false)
    }

    const handleSaveRetroactivePitch = () => {
        if (!retroactiveCampaign || !retroactiveCampaign.id || !retroactiveDate || !retroactiveNote.trim()) {
            return
        }

        setRetroactiveSubmitting(true)

        callAPI(`affiliate/${affiliateId}/pitches/retroactive`, 'POST', {
            offerId: retroactiveCampaign.id,
            offerName: retroactiveCampaign.name,
            pitchDate: retroactiveDate,
            followupNote: retroactiveNote.trim(),
        })
            .then((result) => {
                if (result['success'] === true) {
                    showSystemMessage('success', result.message)
                    closeRetroactiveModal()
                    setDataLoaded(false)
                } else {
                    showSystemMessage('error', result.errors ? result.errors.message : 'There was an error while saving the retroactive pitch.')
                    setRetroactiveSubmitting(false)
                }
            })
            .catch((error) => {
                console.log('Error while saving retroactive pitch: ', error)
                showSystemMessage('error', 'Connection Error')
                setRetroactiveSubmitting(false)
            })
    }

    const handleToggleOverride = (item) => {
        const override = item.override !== 'Y'

        callAPI(`affiliate/pitches/${item.pitch_id}/override`, 'POST', { override })
            .then((result) => {
                if (result['success'] === true) {
                    showSystemMessage('success', result.message)
                    setPitchList((prev) => prev.map((pitch) => (pitch.pitch_id === item.pitch_id ? { ...pitch, override: override ? 'Y' : null } : pitch)))
                } else if (result['success'] === false) {
                    showSystemMessage('error', result.errors.message)
                }
            })
            .catch((error) => {
                console.log('Error while updating pitch override: ', error)
                showSystemMessage('error', 'Connection Error')
            })
    }

    const handleDeleteConfirmation = () => {
        setDataLoaded(false)

        let url = `affiliate/pitches`

        callAPI(url, 'DELETE', { pitchId: selectedOffer })
            .then((result) => {
                if (result['success'] === true) {
                    showSystemMessage('success', result.message)
                    // setDataLoaded(true);
                } else if (result['success'] === false) {
                    if (result.code !== 401) {
                        showSystemMessage('error', 'There was an error while deleting the pitch. Please try again')
                    }
                } else {
                    showSystemMessage('error', result.errors.message)
                }
            })
            .catch((error) => {
                console.log('Error in Affiliate pitch delete: ', error)
                showSystemMessage('error', 'Connection Error')
            })
    }

    return (
        <div className="card mb-2">
            <Modal show={!!followupModalData} onHide={closeFollowupNoteModal} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Follow up note</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {followupModalData?.name && followupModalData?.offerId && <><Campaign oid={followupModalData.offerId} name={followupModalData.name} /><hr/></>}

                    <span className="font-weight-bold">{moment.utc(followupModalData?.date).format('YYYY/MM/DD HH:mm:ss')}</span><br></br>
                    <div dangerouslySetInnerHTML={{ __html: followupModalData?.note }}></div>
                </Modal.Body>
            </Modal>
            <Modal show={showRetroactiveModal} onHide={closeRetroactiveModal} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Add retroactive pitch</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex align-items-center mb-2">
                        <span className="w-25">Campaign:</span>
                        <div className="w-75">
                            <CampaignSearch setCampaign={setRetroactiveCampaign} campaign={retroactiveCampaign && retroactiveCampaign.id > 0 ? retroactiveCampaign : false} affiliateId={affiliateId} placeholder="Search campaigns..." />
                        </div>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                        <span className="w-25">Pitch date:</span>
                        <div className="w-75">
                            <DatePicker placeholderText="Choose a date" className="form-control form-control-sm" selected={retroactiveDate ? moment(retroactiveDate, 'YYYY-MM-DD').toDate() : null} onChange={(date) => setRetroactiveDate(date ? moment(date).format('YYYY-MM-DD') : '')} />
                        </div>
                    </div>
                    <div className="d-flex mb-2">
                        <span className="w-25 pt-1">Followup note:</span>
                        <div className="w-75">
                            <textarea className="form-control" placeholder="Explain that this is being added retroactively..." value={retroactiveNote} onChange={(event) => setRetroactiveNote(event.target.value)} rows={4} />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton variant="primary" name="Save" onClick={handleSaveRetroactivePitch} spin={retroactiveSubmitting} disabled={!retroactiveCampaign || !retroactiveCampaign.id || !retroactiveDate || !retroactiveNote.trim()} />
                </Modal.Footer>
            </Modal>
            <div className="align-items-center p-2 card-header">
                <div className="font-weight-bold float-left">Campaign Pitches (Last 3 months)</div>
                <div className="float-right d-flex align-items-center">
                    {showOverrideToggle && (
                        <a
                            href="#add-retroactive"
                            className="text-primary mr-4"
                            onClick={(event) => {
                                event.preventDefault()
                                setShowRetroactiveModal(true)
                            }}
                        >
                            Add retroactive
                        </a>
                    )}
                    <Link to={`/reports/custom/campaignPitchReport?affiliateId=${affiliateId}&navigation=affiliate`} className="text-primary">
                        List All
                    </Link>
                </div>
            </div>
            <div className="card-body p-0">
                <div>
                    {dataLoaded ? (
                        pitchList.length === 0 ? (
                            <div className="p-2">No campaign pitches available</div>
                        ) : (
                            <table className="table table-striped table-card table-hover">
                                <tbody>
                                    {pitchList.map((item) => {
                                        let element

                                        if (focusedOffer === item.pitch_id && !selectedOffer) {
                                            element = <Trash2 key={focusedOffer} size={16} color="red" onClick={() => handleDelete(item.pitch_id)} className="ml-2"/>
                                        } else if (focusedOffer === item.pitch_id && selectedOffer === item.pitch_id) {
                                            element = (
                                                <button type="button" className="btn btn-sm btn-outline-danger ml-2" onClick={handleDeleteConfirmation}>
                                                    Confirm
                                                </button>
                                            )
                                        }

                                        return (
                                            <tr key={item.pitch_id} onMouseEnter={() => handleMouseEnter(item.pitch_id)} onMouseLeave={() => handleMouseLeave(item.pitch_id)} className="align-items-center">
                                                <td>
                                                    <div className='d-flex align-items-center'>
                                                        <span style={{ textDecoration: item.override === 'Y' ? 'line-through' : 'none' }}>
                                                            <Campaign oid={item.offer_id} name={item.name} status={item.status} affiliateId={affiliateId} />
                                                        </span>
                                                        {item.followup_id && <Info size={14} color="#007bff" onClick={() => handleViewFollowupNote(item)} className="ml-2" style={{ cursor: 'pointer' }} />}
                                                        {element}
                                                    </div>
                                                </td>
                                                <td className="text-right text-nowrap">
                                                    <div className="d-inline-flex align-items-center justify-content-end">
                                                        {showOverrideToggle && <span className="border rounded px-1"><Checkbox id={`override-${item.pitch_id}`} name="Override" checkboxChanged={() => handleToggleOverride(item)} isChecked={item.override === 'Y'} /></span>}
                                                        <span className="ml-1">{moment(item.db_date).format('YYYY-MM-DD')}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        )
                    ) : (
                        <Loading></Loading>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CampaignPitches
